import React from 'react';
import { Form, Modal, Input, Drawer, theme } from 'antd';
import { t } from 'i18next';
import { getDrawerStyles } from './CustomerInformationContent.styles';
import { EPButton } from '@/components';
import { useResponsive } from '@/hooks/useResponsive';
import { FormFieldConfig } from './CustomerInformationContent.utils';
import { SiteAddressData, ContactPersonData } from '@/store/SiteSurveyForm/constants';

const { useToken } = theme;

export interface DynamicEditModalProps {
  visible: boolean;
  onClose: () => void;
  onSave: (values: SiteAddressData | ContactPersonData) => void;
  title: string;
  description?: string;
  fields: FormFieldConfig[];
  fieldNamePath: (string | number)[];
}

const DynamicEditModal: React.FC<DynamicEditModalProps> = ({
  visible,
  onClose,
  onSave,
  title,
  description,
  fields,
  fieldNamePath,
}) => {
  const { token } = useToken();
  const { isXl } = useResponsive();
  const form = Form.useFormInstance();

  const handleSave = async () => {
    if (!form) {
      onClose();
      return;
    }

    try {
      const fieldsToValidate = fields.map((field) => [...fieldNamePath, field.name]);
      await form.validateFields(fieldsToValidate);

      const formData = form.getFieldValue(fieldNamePath);
      onSave(formData);
      onClose();
    } catch (errorInfo) {
      console.error('Validation failed:', errorInfo);
    }
  };

  const renderFormField = (field: FormFieldConfig): React.ReactNode => {
    const { name, label, type = 'input', placeholder, rules = [] } = field;

    switch (type) {
      case 'input':
      default:
        return (
          <Form.Item 
            key={name} 
            name={[...fieldNamePath, name]} 
            label={label} 
            rules={rules}
            layout="vertical"
          >
            <Input placeholder={placeholder} />
          </Form.Item>
        );
    }
  };

  const renderForm = (): React.ReactNode => {
    return (
      <div>
        {description && <div>{description}</div>}
        {fields.map(renderFormField)}
      </div>
    );
  };

  if (isXl) {
    return (
      <Modal
        title={title}
        open={visible}
        onCancel={onClose}
        footer={[
          <EPButton key="save" type="primary" onClick={handleSave}>
            {t('Save changes')}
          </EPButton>,
        ]}
      >
        {renderForm()}
      </Modal>
    );
  }

  return (
    <Drawer
      title={title}
      placement="bottom"
      onClose={onClose}
      open={visible}
      height="auto"
      closable={true}
      key={`dynamic-drawer-${!isXl ? 'mobile' : 'desktop'}`}
      styles={getDrawerStyles(token)}
    >
      {renderForm()}
      <EPButton type="primary" onClick={handleSave} block>
        {t('Save changes')}
      </EPButton>
    </Drawer>
  );
};

export default DynamicEditModal;
